// runStatus — the single vocabulary for every `bbpro_rule_runs.status` literal
// this app renders. One entry per status: a plain-English label, what it
// means, and (when there's something an operator can do) how to fix it.
//
// TypeScript twin of backend/engine/run_status.py. Both are pinned together
// by tests/engine/test_run_status_vocabulary.py, which reads this file as
// text and asserts the key sets match — keep the two in lock-step by hand.
//
// #64: the owner's own run row read "blocked_rollout_stage" with no
// explanation. Every screen that shows a run status (Runs.tsx, Rules.tsx,
// CommandCenter.tsx, RulesPreflight.tsx) should read this table instead of
// printing the raw literal.
//
// Test runs (2026-09-23). A dry run writes the SAME status literals as a live
// one, so 'success' on a test run means "these records WOULD change". The
// owner read a dry-run-only Bid Optimizer fire as "Changed records" next to a
// "dry-run" chip. Entries whose live wording says something changed carry a
// `dryRun` variant; every caller passes the run's dry_run flag. The key set is
// unchanged, and the Python twin carries the same variants as `dry_run`
// (tests/engine/test_run_status_vocabulary.py pins that they match).

import type { BadgeTone } from '../components/ui'
import type { PreflightRunStatusClass } from './api'

/** Wording for a test (dry-run) run whose live wording would claim a change. */
export type RunStatusTestWording = {
  label: string
  meaning: string
  tone?: BadgeTone
}

export type RunStatusEntry = {
  cls: PreflightRunStatusClass
  label: string
  meaning: string
  fix?: string
  tone: BadgeTone
  dryRun?: RunStatusTestWording
}

/** The sentence every test-run explanation starts with. */
export const TEST_MODE = 'Preview only: nothing was sent to Amazon.'

/**
 * Whether a run was a test (dry) run. NULL counts as a test — the same
 * convention as the backend's `COALESCE(r.dry_run, TRUE)` (main.py
 * _RUN_SELECT), so no screen claims a change was real when the flag is missing.
 */
export function isTestRun(dryRun: boolean | null | undefined): boolean {
  return dryRun !== false
}

export const RUN_STATUS: Record<string, RunStatusEntry> = {
  running: {
    cls: 'running', tone: 'warning',
    label: 'Running',
    meaning: 'The rule is executing right now.',
  },
  success: {
    cls: 'ok', tone: 'success',
    label: 'Changed records',
    meaning: 'Ran cleanly and changed at least one record.',
    dryRun: {
      tone: 'info',
      label: 'Would change records',
      meaning: `${TEST_MODE} These are the records it would have changed.`,
    },
  },
  success_no_records: {
    cls: 'ok', tone: 'info',
    label: 'No matches',
    meaning: 'Ran cleanly; nothing matched the filter.',
  },
  errors: {
    cls: 'fail', tone: 'danger',
    label: 'Some records failed',
    meaning: 'Some records failed while the rest of the run succeeded.',
    dryRun: {
      label: 'Test — some records failed',
      meaning: `${TEST_MODE} Some records hit an error while the preview worked out their change; the rest would have changed.`,
    },
  },
  failed: {
    cls: 'fail', tone: 'danger',
    label: 'Run failed',
    meaning: 'The run failed outright.',
    dryRun: {
      label: 'Test run failed',
      meaning: `${TEST_MODE} The preview run failed before it could finish.`,
    },
  },
  completed: {
    cls: 'ok', tone: 'info',
    label: 'Completed',
    meaning: 'Finished. The tree runner writes this status even when it changed zero records — check the Records column.',
    dryRun: {
      label: 'Test finished',
      meaning: `${TEST_MODE} The preview finished; the Records column shows how many records it would have changed.`,
    },
  },
  compile_error: {
    cls: 'fail', tone: 'danger',
    label: "Rule couldn't compile",
    meaning: "The rule's filter or action settings couldn't compile.",
    fix: 'Open the rule and fix the highlighted field, then save again.',
  },
  circuit_open: {
    cls: 'fail', tone: 'danger',
    label: 'Stopped — circuit breaker',
    meaning: 'The safety circuit breaker tripped and stopped the run.',
    fix: 'Check recent error rates for this rule before re-enabling it.',
  },
  blast_radius_exceeded: {
    cls: 'fail', tone: 'danger',
    label: 'Stopped — spend cap',
    meaning: 'The run would have changed more than its spend/record cap allows, so it stopped before dispatching anything.',
    fix: 'Raise the cap deliberately, or narrow the filter.',
  },
  abandoned: {
    cls: 'fail', tone: 'danger',
    label: 'Interrupted by restart',
    meaning: 'The run was interrupted mid-flight, usually by a server restart.',
    fix: 'Re-run the rule — nothing was left half-applied.',
  },
  invalid_params: {
    cls: 'fail', tone: 'danger',
    label: 'Bad action settings',
    meaning: "The action's settings are invalid for this data source.",
    fix: 'Open the rule and correct the action parameters.',
  },
  cross_source_not_supported: {
    cls: 'fail', tone: 'danger',
    label: 'Unsupported data source',
    meaning: 'This filter mixes data sources the action cannot read together.',
    fix: 'Scope the filter to a single data source.',
  },
  skipped_stale_data: {
    cls: 'skip', tone: 'warning',
    label: 'Skipped — data stale',
    meaning: 'Skipped because the underlying data was too old to trust.',
    fix: 'Wait for the next data refresh, or check the sync job.',
  },
  cap_hit: {
    cls: 'skip', tone: 'warning',
    label: 'Stopped — record cap',
    meaning: 'Stopped after hitting its per-fire or per-day record cap.',
    fix: 'Raise the cap on the rule if this was expected.',
  },
  needs_confirmation: {
    cls: 'skip', tone: 'warning',
    label: 'Waiting for admin confirm',
    meaning: 'Parked, waiting for an admin to confirm the live fire.',
    fix: 'An admin can release it here.',
  },
  confirmed_dispatched: {
    cls: 'ok', tone: 'info',
    label: 'Confirmed by admin',
    meaning: 'An admin confirmed this parked run and it dispatched live.',
  },
  blocked_rollout_stage: {
    cls: 'skip', tone: 'warning',
    label: 'Blocked — live writes off',
    meaning: 'Automations were not allowed to write to Amazon, so a safety check held the run back and nothing was sent.',
    fix: 'An admin can switch on "Automations can write to Amazon" in Settings › Advanced.',
  },
  blocked_write_switch: {
    cls: 'skip', tone: 'warning',
    label: 'Blocked — a write switch is off',
    meaning: "The rule fired live, but every write to Amazon was held back by a switch that is off (ADBETTER_LIVE_WRITES, ADBETTER_RULE_WRITES, the rollout stage, emergency stop or AMC activation). Nothing was sent; the run's message names each switch.",
    fix: 'Settings › Advanced shows every switch and which one is off (admins).',
  },
  amc_disabled: {
    cls: 'skip', tone: 'warning',
    label: 'Skipped — AMC off',
    meaning: 'Skipped because AMC (Amazon Marketing Cloud) is not enabled for this account.',
    fix: 'Enable AMC under Settings, or remove the AMC-dependent condition.',
  },
  // CONTRACTS 4.4, verbatim (the automations port, E10).
  skipped_after_failure: {
    cls: 'skip', tone: 'neutral',
    label: 'Skipped after a failed run',
    meaning: 'Skipped because the run before it failed, and this automation is set to skip a run whose predecessor failed.',
    fix: 'Open Activity, see why the earlier run failed, and fix that first.',
  },
  window_restored: {
    cls: 'ok', tone: 'success',
    label: 'Window closed, put back',
    meaning: 'A time window closed and ClickPay put back what the window changed, leaving alone any record someone changed in the meantime.',
  },
  window_restore_pending: {
    cls: 'skip', tone: 'warning',
    label: 'Waiting to put back',
    meaning: "A time window's changes could not all be put back yet (a write switch is off, the circuit breaker is tripped, or a record failed). ClickPay tries again every 5 minutes and does not open the window again until they are back.",
    fix: 'Safety check shows which switch is off. Nothing else is needed: the restore goes out on the next try once writes are allowed.',
  },
}

/** Class-derived sets — mirrors the Python twin's OK_STATUSES/FAIL_STATUSES/SKIP_STATUSES. */
export const OK_STATUSES: ReadonlySet<string> = new Set(
  Object.keys(RUN_STATUS).filter(k => RUN_STATUS[k].cls === 'ok'),
)
export const FAIL_STATUSES: ReadonlySet<string> = new Set(
  Object.keys(RUN_STATUS).filter(k => RUN_STATUS[k].cls === 'fail'),
)
export const SKIP_STATUSES: ReadonlySet<string> = new Set(
  Object.keys(RUN_STATUS).filter(k => RUN_STATUS[k].cls === 'skip'),
)

/** Same "parked" statuses main.py's _classify_run_status treats as parked_safety
 *  (backend/engine/run_status.py PARKED_STATUSES). The two blocked statuses joined
 *  2026-09-17 so a live rule held back by a switch never reads as a green "ok". */
export const PARKED_STATUSES: ReadonlySet<string> = new Set([
  'cap_hit', 'needs_confirmation', 'blocked_rollout_stage', 'blocked_write_switch',
])

export function classifyRunStatus(status: string | null | undefined): PreflightRunStatusClass | 'other' {
  if (!status) return 'other'
  const entry = RUN_STATUS[status]
  return entry ? entry.cls : 'other'
}

/** One row per status, in the vocabulary's canonical order — for enum filters.
 *  A status filter matches live AND test runs, so a status with test wording
 *  names both ("Changed records / Would change records"). */
export const RUN_STATUS_FILTER_OPTIONS: { value: string; label: string }[] = Object.keys(RUN_STATUS).map(
  value => {
    const e = RUN_STATUS[value]
    return { value, label: e.dryRun ? `${e.label} / ${e.dryRun.label}` : e.label }
  },
)

const TONE_PILL_CLASS: Record<BadgeTone, string> = {
  success: 'bg-success-soft text-success-soft-fg',
  info: 'bg-info-soft text-info-soft-fg',
  danger: 'bg-danger-soft text-danger-soft-fg',
  warning: 'bg-warning-soft text-warning-soft-fg',
  neutral: 'bg-surface-3 text-ink-secondary',
  accent: 'bg-accent-soft text-accent-soft-fg',
}

/** Soft pill background + text classes for a tone (semantic tokens). */
export function tonePillClass(tone: BadgeTone): string {
  return TONE_PILL_CLASS[tone]
}

/** Same class strings format.ts's statusColor returned before this module
 *  existed. `dryRun` picks a test run's tone (a test 'success' is info, not
 *  the green of a real change). */
export function runStatusPillClass(status: string | null | undefined, dryRun = false): string {
  return TONE_PILL_CLASS[describeRunStatus(status, dryRun).tone]
}

/**
 * Label + tooltip text + tone for one run status. Pass the run's dry_run
 * (through isTestRun) as `dryRun`: a test run's 'success' reads "Would change
 * records", never "Changed records". Omitted, it describes a live run. An
 * unknown or null status degrades to the raw literal (never throws), so a
 * future runner status that hasn't been added here yet still renders
 * something instead of crashing.
 */
export function describeRunStatus(
  status: string | null | undefined, dryRun = false,
): { label: string; title: string; tone: BadgeTone } {
  const entry = status ? RUN_STATUS[status] : undefined
  if (!entry) return { label: status ?? '—', title: status ?? '', tone: 'neutral' }
  const test = dryRun ? entry.dryRun : undefined
  const meaning = test ? test.meaning : entry.meaning
  const title = entry.fix ? `${meaning} ${entry.fix}` : meaning
  return { label: test ? test.label : entry.label, title, tone: test?.tone ?? entry.tone }
}
